<template>
    <div :class="`multiple-choice multiple-choice-align-${align}`">
        <label :for="componentId">
            {{ fieldLabel }}
            <span v-if="showAsterisk" style="color: red;">*</span>
        </label>
        <div class="checkbox-group">
            <div v-for="(item, index) in items" :key="itemIds[index]" class="checkbox-wrapper">
                <input
                    type="checkbox"
                    :id="itemIds[index]"
                    :name="componentId"
                    :value="item"
                    :checked="selectedValues.some(val => val.value === item && val.id === itemIds[index])"
                    @change="updateSelection(item, $event, itemIds[index])"
                />
                <label :for="itemIds[index]" v-html="processItem(item)"></label>
            </div>
            <div v-if="otherOption" class="checkbox-wrapper">
                <input
                    type="checkbox"
                    :id="`other-${componentId}`"
                    :name="componentId"
                    value="other"
                    v-model="hasOtherSelected"
                />
                <label :for="`other-${componentId}`">{{ otherOption }}</label>
                <input
                    v-show="hasOtherSelected"
                    type="text"
                    v-model="otherText"
                    @input="handleOtherTextChange"
                    class="other-input"
                    placeholder="Diğer seçeneği girin"
                />
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "MultipleSelection",
    props: {
        fieldLabel: {
            type: String,
            required: true,
        },
        align: {
            type: String,
            default: "top",
        },
        items: {
            type: Array,
            required: true,
            default: () => [],
        },
        isRequired: {
            type: Boolean,
            default: false,
        },
        value: {
            type: Array,
            default: () => [],
        },
        showError: {
            type: Boolean,
            default: false,
        },
        formId: {
            type: String,
            required: true,
        },
        order: {
            type: Number,
            required: true,
        },
        otherOption: {
            type: String,
            default: null,
        }
    },
    data() {
        return {
            selectedValues: [],
            otherText: "",
            hasOtherSelected: false,
            componentId: `multiple-choice-${Math.random().toString(36).substring(2, 15)}`,
            itemIds: []
        };
    },
    computed: {
        showAsterisk() {
            return this.isRequired && this.showError && this.selectedValues.length === 0;
        }
    },
    methods: {
        generateItemIds() {
            this.itemIds = this.items.map(() => `checkbox-${this.componentId}-${Math.random().toString(36).substring(2, 15)}`);
        },
        updateSelection(item, event, itemId) {
            const isChecked = event.target.checked;
            if (isChecked) {
                this.selectedValues.push({
                    value: item,
                    id: itemId
                });
            } else {
                this.selectedValues = this.selectedValues.filter(val => val.id !== itemId);
            }
            this.emitSelected();
        },
        handleOtherChange(event) {
            this.hasOtherSelected = event.target.checked;
            if (!this.hasOtherSelected) {
                this.otherText = "";
            }
            this.emitSelected();
        },
        handleOtherTextChange() {
            if (this.hasOtherSelected) {
                this.emitSelected();
            }
        },
        emitSelected() {
            let values = this.selectedValues.map(item => item.value);
            if (this.hasOtherSelected && this.otherText) {
                values.push(this.otherText);
            }
            this.$emit("input", values);
            this.$emit("validate", {
                fieldId: this._uid,
                hasError: this.isRequired && values.length === 0,
            });
        },
        processItem(item) {
            const div = document.createElement('div');
            div.innerHTML = item;
            const links = div.querySelectorAll('a');
            links.forEach(link => {
                if (!link.hasAttribute('target')) {
                    link.setAttribute('target', '_blank');
                }
            });
            return div.innerHTML;
        }
    },
    watch: {
        value: {
            immediate: true,
            handler(newValue) {
                if (newValue && newValue.length > 0) {
                    this.selectedValues = [];
                    
                    newValue.forEach(val => {
                        if (this.items.includes(val)) {
                            const itemIndex = this.items.findIndex(item => item === val);
                            if (itemIndex !== -1) {
                                this.selectedValues.push({
                                    value: val,
                                    id: this.itemIds[itemIndex]
                                });
                            }
                        }
                    });

                    const otherValue = newValue.find(val => !this.items.includes(val));
                    if (otherValue) {
                        this.hasOtherSelected = true;
                        this.otherText = otherValue;
                    }
                } else {
                    this.selectedValues = [];
                    this.hasOtherSelected = false;
                    this.otherText = "";
                }
            }
        }
    },
    created() {
        this.generateItemIds();
    }
};
</script>

<style scoped lang="scss">
.multiple-choice-align-top, .multiple-choice-align-left, .multiple-choice {
    .checkbox-wrapper {
        position: relative;

        &.error input {
            border: 1px solid red;
        }

        .error-message {
            color: red;
            font-size: 10px;
            position: absolute;
            bottom: -18px;
            left: 0;
        }
    }

    label {
        font-size: 18px;
        font-weight: normal;
        color: #2c3e50;
    }
}

.multiple-choice-align-left {
    display: flex;
    align-items: center;

    > label {
        font-size: 16px !important;
        width: 150px !important;
        text-align: left;
        margin-right: 5px;
    }

    .checkbox-group {
        margin-top: 0px !important;
        display: flex;
        gap: 8px;
    }
}

.multiple-choice-align-right {
    display: flex;
    align-items: center;

    > label {
        font-size: 16px !important;
        width: 150px !important;
        text-align: right;
        margin-right: 5px;
    }

    .checkbox-group {
        margin-top: 0px !important;
        width: 100%;
        display: flex;
        gap: 8px;
    }
}

.multiple-choice {
    max-width: 600px;
    margin: 10px auto;
    text-align: left;

    .checkbox-group {
        margin-top: 10px;
        display: flex;
        flex-direction: column;
        gap: 8px;

        @media (max-width: 600px) {
            flex-direction: column;
        }
    }

    .checkbox-wrapper {
        display: flex;
        align-items: center;

        input[type="checkbox"] {
            margin-right: 8px;
        }

        label {
            font-size: 16px;
            color: #8696a0;
        }

        .other-input {
            margin-left: 8px;
            padding: 4px;
            border: none;
            border-bottom: 1px solid #ccc;
            font-size: 14px;
            outline: none;
            background: transparent;

            &:focus {
                border-bottom: 2px solid #1890ff;
            }
        }
    }
}
</style>